import React, { useMemo, useState } from "react";
import api from "@/lib/api";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/ui-helpers";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { UserPlus, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", whatsapp: "", instagram: "", address: "", city: "" };

/**
 * Compact inline "Pelanggan Baru" form used from the order editor.
 * Duplicate protection is enforced server-side (POST /api/customers/quick):
 *  - same WhatsApp / Instagram  -> existing customer is selected instead
 *  - same name only             -> warning with "Pakai yang ada" / "Tetap buat baru"
 */
export function QuickCustomerDialog({ open, onOpenChange, initialName = "", onResolved, testId = "quick-customer" }) {
  const [form, setForm] = useState({ ...EMPTY, name: initialName });
  const [saving, setSaving] = useState(false);
  const [nameClash, setNameClash] = useState(null); // {message, candidates}

  React.useEffect(() => {
    if (open) { setForm({ ...EMPTY, name: initialName || "" }); setNameClash(null); }
  }, [open, initialName]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (force = false) => {
    if (!form.name.trim()) return toast.error("Nama pelanggan wajib diisi");
    setSaving(true);
    try {
      const { data } = await api.post("/customers/quick", { ...form, force });
      if (data.created) {
        toast.success(`Pelanggan "${data.customer.name}" dibuat & dipilih`);
      } else {
        const by = data.matched_by === "whatsapp" ? "Nomor WhatsApp" : "Instagram";
        toast.info(`${by} sudah terdaftar atas nama "${data.customer.name}" — pelanggan tersebut dipilih`);
      }
      onResolved && onResolved(data.customer, data.created);
      onOpenChange(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "NAME_EXISTS") {
        setNameClash({ message: d.message, candidates: d.candidates || [] });
      } else {
        toast.error(typeof d === "string" ? d : "Gagal menyimpan pelanggan");
      }
    } finally { setSaving(false); }
  };

  const pickExisting = (c) => {
    toast.success(`Memakai pelanggan "${c.name}"`);
    onResolved && onResolved(c, false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid={`${testId}-dialog`}>
        <DialogHeader><DialogTitle className="flex items-center gap-2"><UserPlus className="h-4 w-4" />Pelanggan Baru</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Nama" required>
            <Input autoFocus value={form.name} onChange={(e) => { set("name", e.target.value); setNameClash(null); }} placeholder="Nama pelanggan" data-testid={`${testId}-name`} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="WhatsApp"><Input value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="08xx / 62xx" inputMode="tel" data-testid={`${testId}-whatsapp`} /></Field>
            <Field label="Instagram"><Input value={form.instagram} onChange={(e) => set("instagram", e.target.value)} placeholder="@username" data-testid={`${testId}-instagram`} /></Field>
          </div>
          <Field label="Alamat"><Input value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Jalan, nomor, kelurahan" data-testid={`${testId}-address`} /></Field>
          <Field label="Kota"><Input value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Kota" data-testid={`${testId}-city`} /></Field>

          {nameClash && (
            <div className="rounded-xl border border-[hsl(var(--warning)/0.4)] bg-[hsl(var(--warning-bg))] p-3 space-y-2" data-testid={`${testId}-name-clash`}>
              <p className="text-sm flex items-start gap-2"><AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--warning))]" />{nameClash.message} Pakai yang sudah ada atau tetap buat baru?</p>
              <div className="space-y-1.5">
                {nameClash.candidates.map((c) => (
                  <div key={c.id} className="flex items-center justify-between gap-2 rounded-lg bg-card border border-border px-2.5 py-1.5">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{c.name}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{[c.whatsapp, c.instagram && `@${c.instagram.replace(/^@/, "")}`, c.city || c.address].filter(Boolean).join(" · ") || "Tanpa detail"}</p>
                    </div>
                    <Button type="button" size="sm" variant="outline" onClick={() => pickExisting(c)} data-testid={`${testId}-use-existing`}>Pakai yang ada</Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid={`${testId}-cancel`}>Batal</Button>
          {nameClash ? (
            <Button type="button" onClick={() => submit(true)} disabled={saving} data-testid={`${testId}-force-create`}>
              {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Tetap Buat Baru
            </Button>
          ) : (
            <Button type="button" onClick={() => submit(false)} disabled={saving} data-testid={`${testId}-save`}>
              {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Simpan & Pilih
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Searchable customer field (name / address / city / Instagram / WhatsApp —
 * partial, case-insensitive, best 50 matches) + inline "Pelanggan Baru".
 */
export function CustomerPicker({ customers = [], value, onChange, onCustomerCreated, testId = "order-customer-select", disabled = false }) {
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickName, setQuickName] = useState("");

  const options = useMemo(() => customers.map((c) => {
    const ig = c.instagram ? `@${String(c.instagram).replace(/^@/, "")}` : "";
    const place = [c.city, c.address].filter(Boolean).join(" · ");
    return {
      value: c.id,
      label: c.name,
      sublabel: [c.whatsapp, ig, place].filter(Boolean).join(" · "),
      keywords: [c.name, c.whatsapp, c.phone, c.instagram, c.email, c.address, c.city, c.province].filter(Boolean),
    };
  }), [customers]);

  const resolved = (customer, created) => {
    if (created) onCustomerCreated && onCustomerCreated(customer);
    else if (!customers.find((c) => c.id === customer.id)) onCustomerCreated && onCustomerCreated(customer);
    onChange(customer.id);
  };

  return (
    <div className="flex gap-2 items-start">
      <div className="flex-1 min-w-0">
        <SearchableSelect
          value={value}
          onChange={onChange}
          options={options}
          placeholder="Cari nama / WA / IG / kota..."
          searchPlaceholder="Ketik nama, alamat, kota, IG, WhatsApp..."
          emptyText="Pelanggan tidak ditemukan"
          testId={testId}
          maxResults={50}
          disabled={disabled}
          onCreateNew={(q) => { setQuickName(q); setQuickOpen(true); }}
          createLabel="Pelanggan baru"
        />
      </div>
      <Button type="button" variant="outline" className="shrink-0" disabled={disabled}
        onClick={() => { setQuickName(""); setQuickOpen(true); }} data-testid={`${testId}-new`}>
        <UserPlus className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Pelanggan Baru</span>
      </Button>
      <QuickCustomerDialog open={quickOpen} onOpenChange={setQuickOpen} initialName={quickName} onResolved={resolved} testId="quick-customer" />
    </div>
  );
}
